import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container, Typography, Card, CardContent, Grid, Box, Button,
  Chip, LinearProgress, Divider, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, InputAdornment, IconButton, Tooltip, Alert, CircularProgress,
  Tabs, Tab, Collapse, Paper
} from '@mui/material';
import {
  Add, Search, FilterList, Visibility, LocationOn, Person,
  AttachMoney, CheckCircle, Edit, Payment, PhotoLibrary, Satellite,
  Warning, Message, Description, Flag, SupervisorAccount, ExpandMore,
  ExpandLess, Clear
} from '@mui/icons-material';
import projectService from '../services/projectService';
import projectManagerApi from '../services/projectManagerApi';
import milestoneService from '../services/milestoneService';
import MilestoneManager from '../components/milestones/MilestoneManager';
import ProgressVisibility from '../components/projects/ProgressVisibility';
import MessagingCenter from '../components/communications/MessagingCenter';
import SatelliteMonitoringPanel from '../components/monitoring/SatelliteMonitoringPanel';
import RiskManagementDashboard from '../components/risk/RiskManagementDashboard';
import ProposalsManager from '../components/ProposalsManager';
import OwnerPMManagement from '../components/projectManager/OwnerPMManagement';
import UnifiedProjectWizard from '../components/projects/UnifiedProjectWizard';
import ProjectSummaryDashboard from '../components/projects/ProjectSummaryDashboard';
import ImprovedProjectCard from '../components/projects/ImprovedProjectCard';
import ProjectFilters from '../components/projects/ProjectFilters';
import DialogCloseButton from '../components/common/DialogCloseButton';
import ProjectsEmptyState from '../components/projects/ProjectsEmptyState';
import { ProjectCardSkeletonGrid } from '../components/projects/ProjectCardSkeleton';
import projectAIService from '../services/projectAIService';

// Sample data removed - now using real database data only

const ProjectsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sortBy, setSortBy] = useState('recent');
  const [enrichedProjects, setEnrichedProjects] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState({
    location: '',
    minBudget: '',
    maxBudget: '',
    dateFrom: '',
    dateTo: ''
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [useNewWizard, setUseNewWizard] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewTemplatesOpen, setViewTemplatesOpen] = useState(false);
  const [createMilestoneOpen, setCreateMilestoneOpen] = useState(false);
  const [createMilestoneWizardOpen, setCreateMilestoneWizardOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [milestoneRefreshKey, setMilestoneRefreshKey] = useState(0);
  const [newMilestone, setNewMilestone] = useState({
    name: '',
    description: '',
    type: 'custom',
    budget: '',
    startDate: '',
    endDate: ''
  });
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    location: '',
    budget: '',
    currency: 'USD',
    contractor: ''
  });
  
  const { user } = useAuth();
  const userId = user?.id;

  // Filter and sort projects
  const filteredProjects = React.useMemo(() => {
    // Use enriched projects if available, otherwise use regular projects
    const projectsToFilter = enrichedProjects.length > 0 ? enrichedProjects : projects;
    
    // Filter
    const filtered = projectsToFilter.filter(project => {
      const titleMatch = project.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const descriptionMatch = project.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const contractorMatch = project.contractor?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSearch = titleMatch || descriptionMatch || contractorMatch;
                           
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      
      // Additional filters
      const matchesLocation = !additionalFilters.location || 
        project.location?.toLowerCase().includes(additionalFilters.location.toLowerCase());
      
      const budget = parseFloat(project.budget || 0);
      const matchesMinBudget = !additionalFilters.minBudget || budget >= parseFloat(additionalFilters.minBudget);
      const matchesMaxBudget = !additionalFilters.maxBudget || budget <= parseFloat(additionalFilters.maxBudget);
      
      const projectDate = new Date(project.created_at || project.createdAt);
      const matchesDateFrom = !additionalFilters.dateFrom || projectDate >= new Date(additionalFilters.dateFrom);
      const matchesDateTo = !additionalFilters.dateTo || projectDate <= new Date(additionalFilters.dateTo + 'T23:59:59');
      
      return matchesSearch && matchesStatus && matchesLocation && 
             matchesMinBudget && matchesMaxBudget && matchesDateFrom && matchesDateTo;
    });
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt);
        case 'oldest':
          return new Date(a.created_at || a.createdAt) - new Date(b.created_at || b.createdAt);
        case 'budget_high':
          return parseFloat(b.budget || 0) - parseFloat(a.budget || 0);
        case 'budget_low':
          return parseFloat(a.budget || 0) - parseFloat(b.budget || 0);
        case 'progress_high':
          return parseFloat(b.progress || 0) - parseFloat(a.progress || 0);
        case 'progress_low':
          return parseFloat(a.progress || 0) - parseFloat(b.progress || 0);
        case 'title_asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title_desc':
          return (b.title || '').localeCompare(a.title || '');
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [projects, enrichedProjects, searchTerm, statusFilter, additionalFilters, sortBy]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('recent');
    setAdditionalFilters({
      location: '',
      minBudget: '',
      maxBudget: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const activeFilterCount = [
    additionalFilters.location,
    additionalFilters.minBudget,
    additionalFilters.maxBudget,
    additionalFilters.dateFrom,
    additionalFilters.dateTo
  ].filter(Boolean).length;

  const getStatusChip = (status) => {
    // Aligned with database values: planning, open_for_bids, in_progress, completed, cancelled
    const statusMap = {
      planning: { label: '📋 Planning', color: 'default' },
      open_for_bids: { label: '📢 Open for Quotations', color: 'info' },
      in_progress: { label: '🔄 In Progress', color: 'primary' },
      completed: { label: '✅ Completed', color: 'success' },
      cancelled: { label: '❌ Cancelled', color: 'error' },
      // Legacy/fallback statuses
      not_started: { label: '📋 Not Started', color: 'default' },
      delayed: { label: '⚠️ Delayed', color: 'warning' },
      on_hold: { label: '⏸️ On Hold', color: 'warning' },
      pending: { label: '⏳ Pending', color: 'default' }
    };
    
    const statusInfo = statusMap[status] || { label: status || 'Unknown', color: 'default' };
    
    return (
      <Chip 
        label={statusInfo.label} 
        color={statusInfo.color} 
        size="small" 
      />
    );
  };

  const formatCurrency = (amount, currency = 'USD') => {
    // Validate inputs
    if (amount === undefined || amount === null) {
      amount = 0;
    }
    if (!currency || typeof currency !== 'string' || currency.length !== 3) {
      currency = 'USD';
    }
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(amount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `$${Number(amount).toFixed(2)}`;
    }
  };

  // Load projects function - defined outside useEffect so it can be called elsewhere
  const loadProjects = async () => {
    try {
      setLoading(true);
      if (!userId) {
        setProjects([]);
        return;
      }

      // Use different API based on user role
      let data;
      if (user?.role === 'project_manager') {
        const response = await projectManagerApi.getMyProjects();
        data = response.data || [];
      } else {
        data = await projectService.getProjects(userId);
      }

      setProjects(data);
      
      // Enrich projects with AI insights
      const enriched = await projectAIService.enrichProjectsWithAI(data);
      setEnrichedProjects(enriched);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Load projects for the current user (persisted in localStorage via projectService)
  useEffect(() => {
    loadProjects();
  }, [userId]);

  // ?create=1 (from "describe my project" links in emails/WhatsApp) opens the
  // wizard straight away — one tap from the message to the first question.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setUseNewWizard(true);
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle view query parameter to navigate to project details page
  useEffect(() => {
    const viewProjectId = searchParams.get('view');
    const editProjectId = searchParams.get('edit');
    const shouldCreateMilestone = searchParams.get('createMilestone') === 'true';
    
    if (projects.length > 0 && !loading) {
      const projectToView = viewProjectId ? projects.find(p => p.id === viewProjectId) : null;
      const projectToEdit = editProjectId ? projects.find(p => p.id === editProjectId) : null;

      if (projectToView) {
        // Navigate to project detail page
        if (shouldCreateMilestone) {
          navigate(`/projects/${viewProjectId}?createMilestone=true`);
        } else {
          navigate(`/projects/${viewProjectId}`);
        }
        // Clear the query parameters
        setSearchParams({});
      } else if (projectToEdit) {
        setSelectedProject(projectToEdit);
        setNewProject({
          title: projectToEdit.title,
          description: projectToEdit.description,
          location: projectToEdit.location,
          budget: projectToEdit.budget,
          currency: projectToEdit.currency,
          contractor: projectToEdit.manual_contractor_name || projectToEdit.contractor?.name || ''
        });
        setEditDialogOpen(true);
        setSearchParams({});
      }
    }
  }, [searchParams, projects, loading, setSearchParams, navigate]);

  const handleCreateProject = async () => {
    if (!userId) {
      setError('User not authenticated');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const result = await projectService.createProject(userId, newProject);
      
      if (result.success) {
        setSuccess(result.message);
        setProjects(prev => [result.data, ...prev]);
        setCreateDialogOpen(false);
        
        // Reset form
        setNewProject({
          title: '',
          description: '',
          location: '',
          budget: '',
          currency: 'USD',
          contractor: ''
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.message || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = (project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setNewProject({
      title: project.title,
      description: project.description,
      location: project.location,
      budget: project.budget,
      currency: project.currency,
      contractor: project.manual_contractor_name || project.contractor?.name || ''
    });
    setEditDialogOpen(true);
  };

  const handleMakePayment = (project) => {
    setSelectedProject(project);
    setPaymentDialogOpen(true);
  };

  const handleUpdateProject = async () => {
    if (!selectedProject || !userId) {
      setError('Project or user not found');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const payload = {
        title: newProject.title?.trim(),
        description: newProject.description?.trim() || '',
        location: newProject.location?.trim(),
        budget: newProject.budget === '' ? 0 : parseFloat(newProject.budget),
        currency: newProject.currency || 'USD',
        manual_contractor_name: newProject.contractor?.trim() || null
      };

      const result = await projectService.updateProject(selectedProject.id, payload);
      const updatedProject = result.data || result.project || {
        ...selectedProject,
        ...payload
      };

      const contractor = updatedProject.contractor_id
        ? selectedProject.contractor
        : (payload.manual_contractor_name
          ? { id: null, name: payload.manual_contractor_name, manual: true }
          : null);

      setProjects(prev => prev.map(p =>
        p.id === selectedProject.id
          ? { ...p, ...updatedProject, contractor }
          : p
      ));
      setEnrichedProjects(prev => prev.map(p =>
        p.id === selectedProject.id
          ? { ...p, ...updatedProject, contractor }
          : p
      ));
      
      setSuccess('Project updated successfully!');
      setEditDialogOpen(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating project:', error);
      setError(error.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateMilestone = async () => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      // Create milestone using milestone service
      const milestoneData = {
        projectId: selectedProject.id,
        milestoneDetails: {
          name: newMilestone.name?.trim(),
          description: newMilestone.description?.trim(),
          type: (newMilestone.type || 'custom').trim()
        },
        financial: {
          budgetAllocation: parseFloat(newMilestone.budget) || 0
        },
        scheduling: {
          plannedStartDate: newMilestone.startDate || null,
          plannedEndDate: newMilestone.endDate || null
        }
      };

      console.log('Submitting milestoneData:', JSON.stringify(milestoneData, null, 2));
      
      const result = await milestoneService.createMilestone(milestoneData);
      
      if (result.success) {
        setSuccess(`Milestone created for "${selectedProject.title}"!`);
        setCreateMilestoneOpen(false);
        setMilestoneRefreshKey(prev => prev + 1);
        
        // Reset form
        setNewMilestone({
          name: '',
          description: '',
          type: 'custom',
          budget: '',
          startDate: '',
          endDate: ''
        });
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error creating milestone:', error);
      setError(error.message || 'Failed to create milestone');
    } finally {
      setSaving(false);
    }
  };

  // Predefined milestone templates
  const milestoneTemplates = [
    {
      name: 'Site Preparation & Foundation',
      description: 'Site clearance, excavation, and foundation work including concrete pour and waterproofing',
      suggestedBudget: '15-20% of total budget',
      timeline: '1-2 weeks'
    },
    {
      name: 'Structural Framing',
      description: 'Wall framing, roof structure installation, and structural integrity work',
      suggestedBudget: '20-25% of total budget', 
      timeline: '2-3 weeks'
    },
    {
      name: 'Roofing Installation',
      description: 'Roof covering, guttering, downpipes, and waterproofing systems',
      suggestedBudget: '10-15% of total budget',
      timeline: '1-2 weeks'
    },
    {
      name: 'Electrical Installation',
      description: 'Electrical panel, wiring, outlets, switches, and safety systems',
      suggestedBudget: '10-15% of total budget',
      timeline: '1-2 weeks'
    },
    {
      name: 'Plumbing Installation', 
      description: 'Water supply, drainage, fixtures, and hot water systems',
      suggestedBudget: '10-15% of total budget',
      timeline: '1-2 weeks'
    },
    {
      name: 'Interior Finishing',
      description: 'Plastering, painting, flooring, doors, windows, and built-in furniture',
      suggestedBudget: '15-25% of total budget',
      timeline: '2-4 weeks'
    },
    {
      name: 'Final Inspection & Handover',
      description: 'Quality inspection, punch list completion, and project handover',
      suggestedBudget: '3-5% of total budget', 
      timeline: '3-5 days'
    }
  ];

  const handleClearMilestones = () => {
    // Clear excessive milestone data
    localStorage.removeItem('eqaya_milestones');
    setMilestoneRefreshKey(prev => prev + 1);
    setSuccess('Milestone data cleared successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleGenerateMilestones = async (projectId, projectType = 'residential') => {
    try {
      setSaving(true);
      setError('');
      
      console.log('Generating milestones for project:', projectId, 'type:', projectType);
      
      // Check if milestoneService is available
      if (!milestoneService || !milestoneService.createMilestonesFromTemplate) {
        throw new Error('Milestone service not available');
      }
      
      const result = await milestoneService.createMilestonesFromTemplate(projectId, projectType);
      
      console.log('Milestone generation result:', result);
      
      if (result.success) {
        setSuccess(`Generated ${result.data.length} milestones for project!`);
        // The MilestoneManager will automatically refresh and show the new milestones
        setTimeout(() => setSuccess(''), 5000);
        setMilestoneRefreshKey(prev => prev + 1); // Force refresh
      } else {
        throw new Error(result.message || 'Failed to generate milestones');
      }
    } catch (error) {
      console.error('Error generating milestones:', error);
      setError(error.message || 'Failed to generate milestones');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setPaymentDialogOpen(false);
    setViewTemplatesOpen(false);
    setCreateMilestoneOpen(false);
    setSelectedProject(null);
    setError('');
    // Reset forms
    setNewProject({
      title: '',
      description: '',
      location: '',
      budget: '',
      currency: 'USD',
      contractor: ''
    });
    setNewMilestone({
      name: '',
      description: '',
      budget: '',
      startDate: '',
      endDate: ''
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading projects...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Projects</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => setUseNewWizard(true)}
        >
          New Project
        </Button>
      </Box>

      {/* Summary Dashboard */}
      <ProjectSummaryDashboard 
        projects={enrichedProjects.length > 0 ? enrichedProjects : projects}
        onFilterChange={({ status, sortBy }) => {
          setStatusFilter(status);
          setSortBy(sortBy);
          // Scroll to projects list
          window.scrollTo({ top: 400, behavior: 'smooth' });
        }}
      />

      {/* Filters */}
      <ProjectFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
        additionalFilters={additionalFilters}
        onAdditionalFiltersChange={setAdditionalFilters}
        onClearFilters={handleClearFilters}
        resultCount={filteredProjects.length}
      />

      {/* Old filters - keeping for backward compatibility but hidden */}
      <Box sx={{ display: 'none' }}>
        <Paper sx={{ mb: 4, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="planning">📋 Planning</MenuItem>
              <MenuItem value="open_for_bids">📢 Open for Bids</MenuItem>
              <MenuItem value="in_progress">🔄 In Progress</MenuItem>
              <MenuItem value="completed">✅ Completed</MenuItem>
              <MenuItem value="cancelled">❌ Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ minWidth: 140 }}
          >
            <FilterList sx={{ mr: 1 }} />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          {activeFilterCount > 0 && (
            <Button 
              variant="text" 
              color="error" 
              onClick={handleClearFilters}
              startIcon={<Clear />}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* Expandable Filters */}
        <Collapse in={showFilters}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Location"
                value={additionalFilters.location}
                onChange={(e) => setAdditionalFilters({...additionalFilters, location: e.target.value})}
                placeholder="Filter by location..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Min Budget"
                type="number"
                value={additionalFilters.minBudget}
                onChange={(e) => setAdditionalFilters({...additionalFilters, minBudget: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Max Budget"
                type="number"
                value={additionalFilters.maxBudget}
                onChange={(e) => setAdditionalFilters({...additionalFilters, maxBudget: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={additionalFilters.dateFrom}
                onChange={(e) => setAdditionalFilters({...additionalFilters, dateFrom: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={additionalFilters.dateTo}
                onChange={(e) => setAdditionalFilters({...additionalFilters, dateTo: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Paper>
      </Box>

      {/* Projects List */}
      {loading ? (
        <Grid container spacing={3}>
          <ProjectCardSkeletonGrid count={6} />
        </Grid>
      ) : filteredProjects.length === 0 ? (
        <ProjectsEmptyState
          type={projects.length === 0 ? 'no_projects' : 'no_results'}
          onCreateProject={() => setUseNewWizard(true)}
          onClearFilters={handleClearFilters}
        />
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
          <Grid item xs={12} sm={6} lg={4} key={project.id}>
            <ImprovedProjectCard
              project={project}
              onEdit={handleEditProject}
              onPayment={handleMakePayment}
            />
          </Grid>
          ))}
        </Grid>
      )}

      {/* Old project cards - keeping for reference but hidden */}
      <Box sx={{ display: 'none' }}>
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                    {project.title}
                  </Typography>
                  {getStatusChip(project.status)}
                </Box>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {project.description}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {project.location}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {project.contractor?.name || 'No Contractor'}
                    {project.contractor?.verified && (
                      <CheckCircle color="primary" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(project.spent, project.currency)} / {formatCurrency(project.budget, project.currency)}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Progress: {project.progress}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={project.progress} 
                  sx={{ mb: 2, height: 8, borderRadius: 1 }}
                />
                
                <Typography variant="caption" color="text.secondary">
                  Last update: {project.lastUpdate}
                </Typography>
              </CardContent>
              <Divider />
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button 
                  size="small" 
                  startIcon={<Visibility />}
                  onClick={() => handleViewDetails(project)}
                >
                  View Details
                </Button>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Tooltip title="Edit Project">
                    <IconButton size="small" onClick={() => handleEditProject(project)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Make Payment">
                    <IconButton size="small" onClick={() => handleMakePayment(project)}>
                      <Payment fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Create Project Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={handleCloseDialog} />
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Project Title"
                fullWidth
                value={newProject.title}
                onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                fullWidth
                value={newProject.location}
                onChange={(e) => setNewProject({...newProject, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Budget"
                type="number"
                fullWidth
                value={newProject.budget}
                onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={newProject.currency}
                  label="Currency"
                  onChange={(e) => setNewProject({...newProject, currency: e.target.value})}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Contractor"
                fullWidth
                value={newProject.contractor}
                onChange={(e) => setNewProject({...newProject, contractor: e.target.value})}
                placeholder="e.g., Tafadzwa Moyo Construction"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateProject}
            disabled={saving || !newProject.title || !newProject.location}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating...
              </Box>
            ) : (
              'Create Project'
            )}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Edit Project Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={handleCloseDialog} />
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Project Title"
                fullWidth
                value={newProject.title}
                onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                fullWidth
                value={newProject.location}
                onChange={(e) => setNewProject({...newProject, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Budget"
                type="number"
                fullWidth
                value={newProject.budget}
                onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={newProject.currency}
                  label="Currency"
                  onChange={(e) => setNewProject({...newProject, currency: e.target.value})}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Contractor"
                fullWidth
                value={newProject.contractor}
                onChange={(e) => setNewProject({...newProject, contractor: e.target.value})}
                placeholder="e.g., Tafadzwa Moyo Construction"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateProject}
            disabled={saving || !newProject.title || !newProject.location}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Saving...
              </Box>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={handleCloseDialog} />
        <DialogTitle>Make Payment</DialogTitle>
        <DialogContent dividers>
          {selectedProject && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment for {selectedProject.title}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Amount: {formatCurrency(selectedProject.budget, selectedProject.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payment details will go here.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained">Confirm Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Templates Dialog */}
      <Dialog
        open={viewTemplatesOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={handleCloseDialog} />
        <DialogTitle>Milestone Templates</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose from these predefined milestone templates or use them as inspiration for your custom milestones.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {milestoneTemplates.map((template, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip label={`Budget: ${template.suggestedBudget}`} size="small" />
                      <Chip label={`Timeline: ${template.timeline}`} size="small" />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setNewMilestone({
                          name: template.name,
                          description: template.description,
                          budget: '',
                          startDate: '',
                          endDate: ''
                        });
                        setViewTemplatesOpen(false);
                        setCreateMilestoneOpen(true);
                      }}
                    >
                      Use This Template
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Milestone Dialog - Contractor Only */}
      {user?.role === 'contractor' && (
        <Dialog
          open={createMilestoneOpen}
          onClose={() => setCreateMilestoneOpen(false)}
          maxWidth="md"
          fullWidth
        >
        <DialogCloseButton onClick={() => setCreateMilestoneOpen(false)} />
        <DialogTitle>
          Create New Milestone
          {selectedProject && (
            <Typography variant="subtitle2" color="text.secondary">
              For: {selectedProject.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Milestone Name"
                fullWidth
                value={newMilestone.name}
                onChange={(e) => setNewMilestone({...newMilestone, name: e.target.value})}
                required
                placeholder="e.g., Foundation Work"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newMilestone.description}
                onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})}
                placeholder="Detailed description of the milestone work..."
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Milestone Type</InputLabel>
                <Select
                  value={newMilestone.type}
                  label="Milestone Type"
                  onChange={(e) => setNewMilestone({ ...newMilestone, type: e.target.value })}
                >
                  <MenuItem value="foundation">Foundation</MenuItem>
                  <MenuItem value="framing">Framing</MenuItem>
                  <MenuItem value="roofing">Roofing</MenuItem>
                  <MenuItem value="electrical">Electrical</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="finishing">Finishing</MenuItem>
                  <MenuItem value="final">Final / Handover</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="Budget Allocation"
                type="number"
                fullWidth
                value={newMilestone.budget}
                onChange={(e) => setNewMilestone({...newMilestone, budget: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                placeholder="5000"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={newMilestone.startDate}
                onChange={(e) => setNewMilestone({...newMilestone, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={newMilestone.endDate}
                onChange={(e) => setNewMilestone({...newMilestone, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateMilestoneOpen(false)} disabled={saving}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateMilestone}
            disabled={saving || !newMilestone.name || !selectedProject}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating...
              </Box>
            ) : (
              'Create Milestone'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Unified Project Creation Wizard */}
      <UnifiedProjectWizard
        open={useNewWizard}
        onClose={() => {
          setUseNewWizard(false);
        }}
        onComplete={(projectId) => {
          setUseNewWizard(false);
          // Reload projects
          loadProjects();
          if (projectId) {
            navigate(`/projects/${projectId}`);
          }
        }}
      />

    </Container>
  );
};

export default ProjectsPage;
